import React, { useState } from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import {
  ApiError,
  GeneralLookup,
  LookupService,
  RiderService,
  sif,
} from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import { useNavigate } from 'react-router-dom';
import LoaderWrapper from 'app/components/CircularLoader/LoaderWrapper';
import CircularLoader from 'app/components/CircularLoader';
import { SpinnerCircularFixed } from 'spinners-react';
import isApiError from 'utils/isApiError';
import { useErrorHandlingSlice } from 'app/error/slice';
import { useDispatch } from 'react-redux';

// Icon URLs from Figma design
const moneyIconUrl =
  'https://www.figma.com/api/mcp/asset/4cf8916c-739b-42bb-90bf-d7898dabf7f0';
const arrowIconUrl =
  'https://www.figma.com/api/mcp/asset/daa30156-a8c9-4f69-84b4-5a55dc14c42f';
const infoIconUrl =
  'https://www.figma.com/api/mcp/asset/2a2e71e4-02cb-4127-a2a0-3b3e11d7e84b';
const checkBenefitUrl =
  'https://www.figma.com/api/mcp/asset/bb0293fd-bf7d-4d69-a060-4b01ee09099e';
const lockIconUrl =
  'https://www.figma.com/api/mcp/asset/f3ce075a-b508-4f0f-9041-250fccf4b1a9';
const riderIconUrl =
  'https://www.figma.com/api/mcp/asset/4cf8916c-739b-42bb-90bf-d7898dabf7f0';

/** Safely convert any API value to a renderable string. */
const safeStr = (val: any): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return val.name ?? val.id ?? '—';
  const s = String(val);
  return s.trim() === '' ? '—' : s;
};

const howItWorksSteps = [
  {
    num: '1',
    title: 'Complete Application',
    desc: 'Fill out the form with rider and parent details',
  },
  {
    num: '2',
    title: 'Submit Payment',
    desc: 'Secure payment of $150 membership fee',
  },
  {
    num: '3',
    title: 'Get Enrolled',
    desc: 'Receive confirmation and access to fund benefits',
  },
];

const benefits = [
  'Access to scholarship opportunities',
  'Priority consideration for awards',
  'Educational resources and support',
  'Networking with equestrian community',
];

type Props = {};

const RiderNewEnrollment = (props: Props) => {
  const navigate = useNavigate();
  const currentUserId = Cookies.get('userId');
  const currentSifId = Cookies.get('sifID');
  const currentSifJoinedDate = new Date();
  const [showLoader, setShowLoader] = React.useState<boolean>(false);
  const { riderGetLandingPageRiderInfo, riderCreateSif } = RiderService;
  const { error: apiError, handleRequest } = useApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const [sifDetails, setSifDetails] = React.useState<any>();

  function getSifDetails(id?: string) {
    handleRequest(riderGetLandingPageRiderInfo(id))
      .then(res => {
        setSifDetails(res);
        setShowLoader(false);
      })
      .catch(() => setError(apiError));
  }

  const [sifLookUps, setSifLookUps] = React.useState<GeneralLookup>();
  const [payerTypes, setPayerTypes] = React.useState<GeneralLookup>();
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();
  const { lookupGetAllLookups } = LookupService;
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [interestedInFreeEarnings, setInterestedInFreeEarnings] =
    React.useState(false);
  const [donationProgram501c3, setDonationProgram501c3] = React.useState(false);

  function getAllLookupData(names?: string) {
    handleRequest(lookupGetAllLookups(names))
      .then(data => {
        if (data) {
          const sifTypeLookup = data.find(item => item.type === 'SIFTYPES');
          if (sifTypeLookup) {
            const defaultSifType = sifTypeLookup.values?.find(
              value => value.name === 'sif-rider',
            );
            if (defaultSifType) {
              setSifLookUps(defaultSifType);
            }
          }
          let varPayerTypes = data?.filter(item => {
            if (item?.type === 'PAYER_TYPES') {
              return item.values;
            }
          });
          let payerTypeObj = varPayerTypes?.[0]?.values?.find(item => {
            if (item?.name === 'Rider') {
              return item ?? {};
            }
          });
          setPayerTypes(payerTypeObj);
        }
      })
      .catch(() => setError(apiError));
  }

  const handlePaymentCancel = () => {
    navigate(-1);
  };

  React.useEffect(() => {
    setShowLoader(true);
    getAllLookupData('SIFTYPES,PAYER_TYPES');
  }, []);

  React.useEffect(() => {
    getSifDetails(currentUserId);
  }, []);

  const createSif = (requestBody?: sif) => {
    if (sifLookUps) {
      setSubmitting(true);
      handleRequest(
        riderCreateSif({
          id: currentSifId,
          riderId: currentUserId,
          interestedInFreeEarnings: interestedInFreeEarnings,
          donationProgram501c3: donationProgram501c3,
          joinedDate: currentSifJoinedDate.toISOString(),
          sifType: sifLookUps,
        }),
      )
        .then(res => {
          if (res && !isApiError(res)) {
            navigate(`/payment/${payerTypes?.id}/${currentUserId}/true/false`);
            Cookies.set('SifCreated', true);
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: res.description,
                show: true,
                errorType: 'error',
              }),
            );
            setSubmitting(false);
          }
        })
        .catch(() => {
          setError(apiError);
          setSubmitting(false);
        });
    }
  };

  const canProceed = agreedToTerms && agreedToPrivacy;

  return (
    <>
      {showLoader ? (
        <LoaderWrapper>
          <CircularLoader />
        </LoaderWrapper>
      ) : (
        <PageWrapper>
          <ContentRow>
            {/* Left Sidebar */}
            <Sidebar>
              {/* Membership Amount Card */}
              <MembershipCard>
                <MembershipIconCircle>
                  <img src={moneyIconUrl} alt="" />
                </MembershipIconCircle>
                <MembershipInfo>
                  <span className="label">Membership Amount</span>
                  <span className="amount">$150</span>
                </MembershipInfo>
                <p className="description">
                  One-time enrollment fee for The Winning Edge Scholarship
                  Incentive Fund
                </p>
              </MembershipCard>

              {/* How It Works Card */}
              {/* <SideCard>
                <SideCardTitle>
                  <img src={infoIconUrl} alt="" />
                  <span>How It Works</span>
                </SideCardTitle>
                <StepList>
                  {howItWorksSteps.map(step => (
                    <StepRow key={step.num}>
                      <StepNum>{step.num}</StepNum>
                      <StepContent>
                        <p className="title">{step.title}</p>
                        <p className="desc">{step.desc}</p>
                      </StepContent>
                    </StepRow>
                  ))}
                </StepList>
              </SideCard> */}

              {/* Your Benefits Card */}
              {/* <SideCard $gray>
                <SideCardTitle>
                  <span>Your Benefits</span>
                </SideCardTitle>
                <BenefitList>
                  {benefits.map((b, i) => (
                    <BenefitRow key={i}>
                      <img src={checkBenefitUrl} alt="" />
                      <span>{b}</span>
                    </BenefitRow>
                  ))}
                </BenefitList>
              </SideCard> */}

              {/* Secure badge */}
              <SecureBadge>
                <img src={lockIconUrl} alt="" />
                <div>
                  <p className="title">Secure &amp; Private</p>
                  <p className="desc">
                    Your information is encrypted and protected
                  </p>
                </div>
              </SecureBadge>
            </Sidebar>

            {/* Main Form Area */}
            <FormArea>
              {/* Rider Details Card */}
              <FormCard>
                <FormCardHeader>
                  <IconBox>
                    <img src={riderIconUrl} alt="" />
                  </IconBox>
                  <h2>Rider Details</h2>
                </FormCardHeader>
                <FieldGrid>
                  <ViewField>
                    <FieldLabel>First Name</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.firstName)}</FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>Team Name</FieldLabel>
                    <FieldValue>
                      {safeStr(sifDetails?.team?.name ?? sifDetails?.team)}
                    </FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>Email Address</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.email)}</FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>Phone Number</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.primaryPhone)}</FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>Rider Division</FieldLabel>
                    <FieldValue>
                      {safeStr(
                        sifDetails?.division?.name ?? sifDetails?.division,
                      )}
                    </FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>School</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.schoolName)}</FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>School Address</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.schoolStreet)}</FieldValue>
                  </ViewField>
                  <ViewField>
                    <FieldLabel>Current Grade</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.currentGrade)}</FieldValue>
                  </ViewField>
                  <ViewField $fullWidth>
                    <FieldLabel>Home Mailing Address</FieldLabel>
                    <FieldValue>{safeStr(sifDetails?.street)}</FieldValue>
                  </ViewField>
                  <ViewField $fullWidth>
                    <FieldLabel>Expected Graduation Year</FieldLabel>
                    <FieldValue>
                      {safeStr(sifDetails?.expectedGraduationYear)}
                    </FieldValue>
                  </ViewField>
                </FieldGrid>
              </FormCard>

              {/* Terms and Conditions Card */}
              <FormCard>
                <TermsTitle>Terms and Conditions</TermsTitle>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={e => {
                      setAgreedToTerms(e.target.checked);
                      setInterestedInFreeEarnings(e.target.checked);
                    }}
                  />
                  <label htmlFor="terms">
                    I agree to the <TermsLink>Terms and Conditions</TermsLink>{' '}
                    of The Winning Edge Scholarship Incentive Fund and
                    understand that the $150 membership fee is non-refundable.
                  </label>
                </CheckboxRow>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={agreedToPrivacy}
                    onChange={e => {
                      setAgreedToPrivacy(e.target.checked);
                      setDonationProgram501c3(e.target.checked);
                    }}
                  />
                  <label htmlFor="privacy">
                    I have read and accept the{' '}
                    <TermsLink>Privacy Policy</TermsLink> and consent to the
                    collection and use of my information as described.
                  </label>
                </CheckboxRow>
              </FormCard>

              {/* Footer row */}
              <FooterRow>
                {/* <RequiredNote>
                  All fields marked with <span className="asterisk">*</span> are
                  required
                </RequiredNote> */}
                <ProceedButton
                  disabled={!canProceed || submitting}
                  onClick={() => canProceed && !submitting && createSif()}
                >
                  {submitting ? (
                    <SpinnerCircularFixed
                      size={18}
                      thickness={180}
                      speed={100}
                      color="rgba(255,255,255,1)"
                      secondaryColor="rgba(255,255,255,0.3)"
                    />
                  ) : (
                    <>
                      Proceed to Payment
                      <img src={arrowIconUrl} alt="" />
                    </>
                  )}
                </ProceedButton>
              </FooterRow>
            </FormArea>
          </ContentRow>
        </PageWrapper>
      )}
    </>
  );
};

const PageWrapper = styled.div`
  min-height: 100%;
  background: transparent;
  padding: 40px 94px 60px;
`;

const ContentRow = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;
`;

/* ── Sidebar ── */
const Sidebar = styled.div`
  width: 390px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MembershipCard = styled.div`
  background: linear-gradient(
    154deg,
    rgb(236, 253, 245) 0%,
    rgb(240, 253, 244) 100%
  );
  border: 1px solid #a4f4cf;
  border-radius: 14px;
  padding: 25px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;

  .description {
    font-size: 13px;
    color: #4a5565;
    line-height: 1.55;
    margin: 0;
    width: 100%;
  }
`;

const MembershipIconCircle = styled.div`
  background: #009966;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1);
  }
`;

const MembershipInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .label {
    font-size: 13px;
    color: #4a5565;
  }

  .amount {
    font-size: 28px;
    font-weight: 700;
    color: #009966;
    line-height: 1.2;
  }
`;

const SideCard = styled.div<{ $gray?: boolean }>`
  background: ${({ $gray }) => ($gray ? '#f9fafb' : '#ffffff')};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SideCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 600;
  color: #0a0a0a;

  img {
    width: 20px;
    height: 20px;
  }
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const StepNum = styled.div`
  background: rgba(2, 68, 250, 0.1);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #0244fa;
`;

const StepContent = styled.div`
  .title {
    font-size: 13px;
    font-weight: 500;
    color: #0a0a0a;
    margin: 0 0 2px;
  }
  .desc {
    font-size: 12px;
    color: #6a7282;
    margin: 0;
    line-height: 1.4;
  }
`;

const BenefitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BenefitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4a5565;

  img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const SecureBadge = styled.div`
  background: #eff6ff;
  border: 1px solid #bedbff;
  border-radius: 10px;
  padding: 17px;
  display: flex;
  gap: 12px;
  align-items: flex-start;

  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .title {
    font-size: 13px;
    font-weight: 500;
    color: #1c398e;
    margin: 0 0 2px;
  }

  .desc {
    font-size: 12px;
    color: #1447e6;
    margin: 0;
  }
`;

/* ── Main Form ── */
const FormArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FormCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 25px;
`;

const FormCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #0a0a0a;
    margin: 0;
  }
`;

const IconBox = styled.div`
  background: rgba(2, 68, 250, 0.1);
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 20px;
    height: 20px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 32px;
`;

const ViewField = styled.div<{ $fullWidth?: boolean }>`
  ${({ $fullWidth }) => $fullWidth && 'grid-column: 1 / -1;'}
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.p`
  font-size: 13px;
  color: #6a7282;
  margin: 0;
  line-height: 1.4;
`;

const FieldValue = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #101828;
  margin: 0;
  line-height: 1.5;
`;

const TermsTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #0a0a0a;
  margin: 0 0 20px;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;

  input[type='checkbox'] {
    margin-top: 3px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
    accent-color: #0244fa;
  }

  label {
    font-size: 12px;
    color: #4a5565;
    line-height: 1.6;
    cursor: pointer;
  }
`;

const TermsLink = styled.span`
  color: #0244fa;
  cursor: pointer;
  text-decoration: underline;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const RequiredNote = styled.p`
  font-size: 13px;
  color: #6a7282;
  margin: 0;

  .asterisk {
    color: #fb2c36;
  }
`;

const ProceedButton = styled.button`
  background: #155dfc;
  color: #ffffff;
  border: none;
  border-radius: 14px;
  padding: 13px 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s, opacity 0.2s;

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: #0235c8;
  }
`;

export default RiderNewEnrollment;
